import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Star, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

interface RatingDialogProps {
  systemId: string;
  existingRating?: { stars: number; comment: string | null; id: string; updated_at?: string } | null;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const EDIT_COOLDOWN_MS = 24 * 60 * 60 * 1000;

function getTimeUntilEditable(updatedAt?: string): { canEdit: boolean; hoursLeft: number; minutesLeft: number } {
  if (!updatedAt) return { canEdit: true, hoursLeft: 0, minutesLeft: 0 };
  const diff = Date.now() - new Date(updatedAt).getTime();
  if (diff >= EDIT_COOLDOWN_MS) return { canEdit: true, hoursLeft: 0, minutesLeft: 0 };
  const remaining = EDIT_COOLDOWN_MS - diff;
  return {
    canEdit: false,
    hoursLeft: Math.floor(remaining / (60 * 60 * 1000)),
    minutesLeft: Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000)),
  };
}

export function RatingDialog({ systemId, existingRating, trigger, open, onOpenChange }: RatingDialogProps) {
  const [stars, setStars] = useState(existingRating?.stars || 5);
  const [comment, setComment] = useState(existingRating?.comment || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  
  const queryClient = useQueryClient();
  
  const isOpen = open !== undefined ? open : internalOpen;
  const setIsOpen = onOpenChange || setInternalOpen;

  const cooldown = existingRating ? getTimeUntilEditable(existingRating.updated_at) : { canEdit: true, hoursLeft: 0, minutesLeft: 0 };

  useEffect(() => {
    if (isOpen) {
      setStars(existingRating?.stars || 5);
      setComment(existingRating?.comment || "");
    }
  }, [isOpen, existingRating]);

  const handleSubmit = async () => {
    if (existingRating && !cooldown.canEdit) {
      toast.error(`ניתן לערוך שוב בעוד ${cooldown.hoursLeft} שעות ו-${cooldown.minutesLeft} דקות`);
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const now = new Date().toISOString();
      const trimmedComment = comment.trim().slice(0, 500);

      const payload = {
        system_id: systemId,
        user_id: user.id,
        stars,
        comment: trimmedComment,
        updated_at: now
      };

      if (existingRating) {
        const { data: updated, error } = await (supabase.from('ratings') as any)
          .update({ 
            stars, 
            comment: trimmedComment, 
            updated_at: now
          })
          .eq('id', existingRating.id)
          .eq('user_id', user.id)
          .select();
        if (error) throw error;
        if (!updated || updated.length === 0) throw new Error("אין הרשאה לעדכן דירוג זה");
      } else {
        const { error } = await supabase
          .from('ratings')
          .insert(payload as any);
        if (error) {
          if (error.code === '23505') {
            toast.error("כבר דירגת מערכת זו. ניתן לדרג רק פעם אחת.");
            setIsOpen(false);
            return;
          }
          if (error.code === 'P0002' || error.message.includes('own system')) {
             throw new Error("לא ניתן לדרג מערכת שאתה יצרת");
          }
          throw error;
        }
      }

      toast.success(existingRating ? "הדירוג עודכן בהצלחה!" : "הדירוג נוסף בהצלחה!");
      
      queryClient.invalidateQueries({ queryKey: ['system'] });
      queryClient.invalidateQueries({ queryKey: ['user-rating', systemId] });
      queryClient.invalidateQueries({ queryKey: ['system-reviews', systemId] });
      queryClient.invalidateQueries({ queryKey: ['my-reviews'] });
      
      setIsOpen(false);
    } catch (error: any) {
      console.error("Rating Error:", error);
      const msg = error.message?.includes("הרשאה") || error.message?.includes("לא ניתן") 
        ? error.message 
        : "שגיאה בשמירת הדירוג";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-[95vw] sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{existingRating ? "עריכת חוות דעת" : "כתיבת חוות דעת"}</DialogTitle>
          <DialogDescription>
            {existingRating 
              ? "ניתן לערוך את חוות הדעת פעם ב-24 שעות."
              : "שתף את החוויה שלך מהשימוש במערכת זו. ניתן לדרג פעם אחת בלבד."
            }
          </DialogDescription>
        </DialogHeader>

        {existingRating && !cooldown.canEdit ? (
          <div className="py-8 flex flex-col items-center text-center gap-3">
            <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center text-slate-400">
              <Clock className="w-7 h-7" />
            </div>
            <h3 className="font-semibold text-slate-900">עריכה תתאפשר בקרוב</h3>
            <p className="text-sm text-slate-500">
              ניתן לערוך שוב בעוד{" "}
              <span className="font-bold text-slate-700">
                {cooldown.hoursLeft > 0 && `${cooldown.hoursLeft} שעות `}
                {cooldown.minutesLeft} דקות
              </span>
            </p>
            <div className="text-xs text-slate-400 mt-1">
              הדירוג הנוכחי: {existingRating.stars} כוכבים
            </div>
          </div>
        ) : (
          <div className="grid gap-4 py-4">
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setStars(value)}
                  className="focus:outline-none transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-10 h-10 sm:w-8 sm:h-8 ${
                      value <= stars
                        ? "fill-amber-400 text-amber-400"
                        : "text-slate-300"
                    }`}
                  />
                </button>
              ))}
            </div>
            <div className="text-center text-sm font-medium text-slate-500 mb-2">
              {stars === 1 && "גרוע מאוד"}
              {stars === 2 && "לא משהו"}
              {stars === 3 && "בסדר"}
              {stars === 4 && "טוב מאוד"}
              {stars === 5 && "מצוין!"}
            </div>
            <Textarea
              placeholder="כתוב כאן את חוות הדעת שלך..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={500}
              className="h-32 resize-none"
            />
            <p className="text-[11px] text-slate-400 text-center">הביקורת תפורסם בפומבי תחת שמך.</p>
          </div>
        )}

        <DialogFooter>
          {(!existingRating || cooldown.canEdit) && (
            <Button type="submit" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "שולח..." : existingRating ? "עדכן דירוג" : "שלח דירוג"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}